/* eslint-disable react/no-unknown-property */
import {
  Card,
  DetailStack, Text, FontWeight, HStack, Icon, ICON_CHECK, ICON_CLOSE, ICON_PLUS,
  CardProps,
} from '@doveit/bricks';
import { formatEuro, formatFloor, formatSquareMeters } from '@doveit/hammer';
import React from 'react';
import { PropertyPreview } from '../../../providers/api/dtos';
import PropertyAvatar from '../property-avatar/PropertyAvatar';
import * as styles from './PropertySelectablePreview.style';
import { NO_VALUE_SYMBOL } from '../../utils';
import EntityReferenceBadge from '../../../components/entity-reference-badge/EntityReferenceBadge';
import { ReferenceType, Status } from '../../../domain/types';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { CardHeaderProps } from '../../../components/card/header/CardHeader';

type PropertySelectablePreviewOperation = 'add' | 'remove';

const mapIconToOperation: { [key in PropertySelectablePreviewOperation]: string } = {
  add: ICON_PLUS,
  remove: ICON_CLOSE,
};

export interface PropertySelectablePreviewBaseProps extends React.AriaAttributes {
  property: Partial<PropertyPreview>,
  asContent?: CardProps['asContent'],
}

export type PropertySelectablePreviewProps = PropertySelectablePreviewBaseProps & ({
  operation?: PropertySelectablePreviewOperation,
  onClick?: VoidFunction,
  selected?: boolean,
  disabled?: boolean,
  index?: number,
  primaryActions?: undefined,
  secondaryActions?: undefined,
} | {
  operation?: undefined,
  onClick?: undefined,
  selected?: undefined,
  disabled?: undefined,
  index?: undefined,
  primaryActions?: CardHeaderProps['primaryActions'],
  secondaryActions?: CardHeaderProps['secondaryActions'],
});

const PropertySelectablePreview: React.FC<PropertySelectablePreviewProps> = ({
  property,
  operation,
  onClick,
  selected = false,
  disabled = false,
  index,
  asContent,
  'aria-label': ariaLabel = 'agent-review-card',
  primaryActions,
  secondaryActions,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');

  const interactive = React.useMemo(
    () => !!onClick,
    [onClick],
  );

  const active = React.useMemo(
    () => selected && !disabled,
    [selected, disabled],
  );

  const propertyAddress = React.useMemo(() => (
    property?.overriddenGeo
      ? [property.overriddenGeo?.route, property.overriddenGeo?.streetNumber]
      : [property?.geo?.route, property?.geo?.streetNumber]).filter((value) => !!value).join(', '), [property]);

  const propertyLocality = React.useMemo(() => (property?.overriddenGeo
    ? property.overriddenGeo?.locality
    : property.geo?.locality), [property]);

  const propertyPrice = React.useMemo(() => {
    if (property?.status === Status.VENDUTO && property?.soldPrice) {
      return formatEuro(property?.soldPrice);
    }

    return property?.price ? formatEuro(property.price) : NO_VALUE_SYMBOL;
  }, [property]);

  const features = React.useMemo(() => ([
    {
      label: 'Superficie',
      value: property?.propertySize ? formatSquareMeters(property.propertySize) : NO_VALUE_SYMBOL,
      width: '3.75rem',
    },
    /*
    {
      label: 'Locali',
      value: rooms ? countRooms(rooms) : NO_VALUE_SYMBOL,
    },
    */
    {
      label: 'Piano',
      value: property?.mainFloor !== undefined ? formatFloor(property?.mainFloor) : NO_VALUE_SYMBOL,
    },
  ]), [property]);

  return (
    <div
      {...rest}
      css={styles.basic}
      $disabled={disabled}
      $interactive={interactive}
      $operation={operation}
      aria-label={ariaLabel}
    >
      <Card
        onClick={(!disabled && onClick) ? onClick : undefined}
        highlighted={active}
        aria-label={`${ariaLabel} (card)`}
        paddingSize="XS"
        asContent={asContent}
      >
        <Card.Header
          primaryActions={[
            <HStack
              gap={100}
              alignItems="center"
              wrap="nowrap"
            >
              {typeof index === 'number' && (
                <div
                  css={styles.index}
                  aria-label={`${ariaLabel} (index)`}
                  $active={active}
                >
                  {index}
                </div>
              )}
              {selected && (
                <div
                  css={styles.icon}
                  $active={active}
                  className="statusIcon"
                  aria-label="Icona stato"
                >
                  <Icon
                    size={18}
                    path={ICON_CHECK}
                  />
                </div>
              )}
              {operation && interactive && (
                <div
                  css={styles.icon}
                  $active={active}
                  className="operationIcon"
                  aria-label="Icona operazione"
                >
                  <Icon
                    size={18}
                    path={mapIconToOperation[operation]}
                  />
                </div>
              )}
            </HStack>,
            ...(primaryActions || []),
          ]}
          secondaryActions={secondaryActions}
        >
          <HStack gap={100} wrap="nowrap">
            <PropertyAvatar
              size="S"
              propertyType={property.propertyType}
            />
            <div>
              <Text.BodySmall
                fontWeight={FontWeight.MEDIUM}
                css={setVerticalEllipsis(1)}
              >
                {propertyAddress}
              </Text.BodySmall>
              <Text.Caption css={setVerticalEllipsis(1)}>
                {propertyLocality}
              </Text.Caption>
            </div>
          </HStack>
        </Card.Header>
        <Card.Content>
          <div css={styles.details}>
            <div>
              <EntityReferenceBadge
                id={property.id!}
                size="XS"
                entity={ReferenceType.PROPERTY}
                label={property?.referenceId}
                isNewTab
              />
            </div>
            <div>
              <DetailStack
                label="Prezzo"
                aria-label={'Anteprima dell\'immobile (prezzo)'}
                size="M"
                width={isMobile ? undefined : '5rem'}
              >
                {propertyPrice}
              </DetailStack>
            </div>
            {features.map((feature) => (
              <div key={`${feature.label}-${feature.value}`}>
                <DetailStack
                  label={feature.label}
                  aria-label={`${feature.label} dell'immobile`}
                  size="M"
                  width={!isMobile && feature.width ? feature.width : undefined}
                >
                  {feature.value}
                </DetailStack>
              </div>
            ))}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default PropertySelectablePreview;
